import React from 'react';
import { ReferenceArrayInput, AutocompleteArrayInput, SelectInput, FunctionField, ReferenceField, ReferenceInput,
    AutocompleteInput, List, Datagrid, TextField, NumberInput, Edit, FormTab, TabbedForm, Toolbar, BooleanInput,
    TextInput, ArrayInput, SimpleFormIterator, SaveButton, DateField } from 'react-admin';
import { SectionHeader } from './components/utils';

const twoColumnGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', width: '50%' };
const fullWidth = { gridColumn: '1 / -1' };

const SettingsTitle = ({ record }) => {
    return <span>Settings Config {record ? `"${record.name}"` : ''}</span>;
};
export const SettingsList = ({ permissions, ...props }) => (
    <List {...props} perPage={25} bulkActionButtons={false}>
        <Datagrid rowClick="edit">
            <TextField source="code" label="ID" />
            <TextField source="name" label="Name" />
            {permissions && permissions.role === 'super_admin' &&
                <ReferenceField source="organization_id" label="Organization" reference="organizations" link={false}>
                    <TextField source="name" />
                </ReferenceField>
            }
        </Datagrid>
    </List>
);
/**
 * 
 * @param {*} props 
 */
export const SettingsEdit = ({ permissions, ...props }) => {
    const isTabDisabled = (id) => !!(permissions && permissions.disabled_features && permissions.disabled_features.includes(id));
    return (<Edit title={<SettingsTitle />} mutationMode="pessimistic" {...props}>
        <TabbedForm toolbar={<CustomToolbar />}>
            {!isTabDisabled('settings_tab_general') && (
            <FormTab label="Settings">
                <div style={twoColumnGrid}>
                    <SectionHeader title="General" />
                    {/* <TextField source="name" label="Name" style={fullWidth} /> */}
                    <div style={fullWidth}>REACT_APP_VERSION: {process.env.REACT_APP_VERSION}</div>
                    <div style={fullWidth}>REACT_APP_API_ENDPOINT: {process.env.REACT_APP_API_ENDPOINT}</div>

                    <SectionHeader title="Feature Flags" />
                    <BooleanInput source="event_streaming_enabled" label="Enable Event Logs" />
                    <BooleanInput source="gc_management_enabled" label="Enable GC Management" />
                    <BooleanInput source="retargeting_enabled" label="Enable Retargeting of Direct Campaigns" />
                    <BooleanInput source="insights_log_enabled" label="Insights Log Enabled" />
                    <BooleanInput source="hourly_revenue_limit_enabled" label="Hourly Revenue Limit Feature Enabled" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="missing_site_logging_enabled" label="Log missing site/app Enabled" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="is_usersync_log_enabled" label="Log User Sync details" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="is_iiq_s2s_log_enabled" label="Log IntentIQ S2S Responses" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="is_traffic_sampling_enabled" label="Enable Traffic Sampling of Bid Requests/Responses and Incoming requests" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="use_nobidviewability_only" label="Use platform viewability data only" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="site_categorization_enabled" label="Site Categorization Enabled" style={{whiteSpace: 'nowrap'}} />
                    <BooleanInput source="referer_defaulting_enabled" label="Referer Defaulting Enabled" style={{whiteSpace: 'nowrap'}} />
                    {/* NBA-38. Master switch for second-price clearing, read per-org by the
                        adserver. On its own it changes nothing: a campaign must ALSO be set to
                        Second price on the campaign form before any auction clears differently.

                        READ THE ORG BEFORE SAVING. IMDBServiceAPI.getSettings(orgId) falls back
                        to org 1's document for any org that has no settings document of its own,
                        so enabling this on org 1 enables it for all of them. Prefer the target
                        org's own document; org 1 is unavoidable for campaigns that live there.

                        Not instant either: settings are served from IMDB, so a change lands on
                        the next reload, not on save. */}
                    <BooleanInput source="is_second_price_clearing_enabled" label="Second Price Clearing Enabled" style={{whiteSpace: 'nowrap'}} />

                    <SectionHeader title="Configuration" />
                    <NumberInput source="cost_adjustment" label="Cost Adjustment ($)" helperText="Additional fixed dollar amount added or substracted in case of negative values to total daily infrastructure cost." />
                    <NumberInput source="adunit_limit" label="Ad Units Limit" />
                    <NumberInput source="default_bid_price" label="Default Bid Price" />
                    <NumberInput source="rtb_request_timeout_millis" label="RTB Request Timeout" />
                    <NumberInput source="imdb_reload_interval_minutes" label="IMDB reload interval (minute)" />
                    <TextInput source="log_level" label="Log Level" />

                    <SectionHeader title="References" />
                    <ReferenceInput label="Publisher Request Log" helperText="Log incoming Bid Requests for this Publisher ID."
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hidden: false }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        source="incoming_bidrequest_log_publisher"
                        reference="publishers"
                        basePath='publishers'
                        allowEmpty emptyText="- None -"
                        style={fullWidth}
                    >
                        <AutocompleteInput optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ""} optionValue="code" />
                    </ReferenceInput>
                    <ReferenceField reference="publishers" source="incoming_bidrequest_log_publisher" label="Selected Publisher Request Log" style={fullWidth}>
                        <FunctionField label="Name" render={record => `${record.name} (${record.code})`} />
                    </ReferenceField>

                    <ReferenceInput label="Default Direct Bidder"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hidden: false }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        source="direct_bidder_id"
                        reference="bidders"
                        basePath='bidders'
                        allowEmpty emptyText="- None -"
                        style={fullWidth}
                    >
                        <AutocompleteInput optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ""} optionValue="code" />
                    </ReferenceInput>

                    <ArrayInput source="video_mimes" label="Default Video Mimes" style={fullWidth}>
                        <SimpleFormIterator>
                            <TextInput source="" label="Video Mime" />
                        </SimpleFormIterator>
                    </ArrayInput>
                </div>
            </FormTab>
            )}
            {!isTabDisabled('settings_tab_trackers') && (
            <FormTab label="Trackers">
                <TextInput fullWidth multiline source="revenue_event_for_banner" label="Revenue Tracker for Banner" />
                <TextInput fullWidth multiline source="revenue_event_for_video" label="Revenue Tracker for Video" />
                <TextInput fullWidth multiline source="revenue_event_for_native" label="Revenue Tracker for Native" />
                <TextInput fullWidth multiline source="click_event_for_native" label="Click Tracker for Native" />
                <TextInput fullWidth multiline source="tracking_events_for_video" label="Tracking Events for Video" />
            </FormTab>
            )}
            {!isTabDisabled('settings_tab_markups') && (
            <FormTab label="Markups">
                <TextInput fullWidth multiline source="markup_config.DEFAULT_banner" label="Default Banner" />
                <TextInput fullWidth multiline source="markup_config.DEFAULT_video" label="Default Video" />
                <TextInput fullWidth multiline source="markup_config.NOBID_DFP_TAG_video" label="Platform DFP Video" />
                <TextInput fullWidth multiline source="markup_config.NOBID_DFP_TAG_banner" label="Platform DFP Banner" />
                <TextInput fullWidth multiline source="creative_nobid_renderTag" label="Tag Renderer for OpenRTB" />
            </FormTab>
            )}
            {!isTabDisabled('settings_tab_native_templates') && (
            <FormTab label="Native Templates">
                <TextInput fullWidth multiline source="native_template.300x600" label="300x600" />
                <TextInput fullWidth multiline source="native_template.300x250" label="300x250" />
                <TextInput fullWidth multiline source="native_template.320x50" label="320x50" />
                <TextInput fullWidth multiline source="native_template.728x90" label="728x90" />
                <TextInput fullWidth multiline source="native_template.970x250" label="970x250" />
                <TextInput fullWidth multiline source="native_template.970x90" label="970x90" />
            </FormTab>
            )}
            {!isTabDisabled('settings_tab_combo') && (
            <FormTab label="Combo">
                <h3>Rendered</h3>
                <TextInput multiline source="combo_configuration.renderer.markup" label="Renderer" style={{ width: "80%" }} />
                <hr style={{width: '100%'}}/>
                
                <h3 style={{width: '100%'}}>Desktop 300x600 (2 * 300x250) Template</h3>
                <TextInput fullwidth multiline source="combo_configuration.300x600.markup" label="300x600" style={{ width: "80%" }} />
                
                <h3 style={{width: '100%'}}>Desktop 970x250 (3 * 300x250) Template</h3>
                <TextInput fullwidth multiline source="combo_configuration.970x250.markup" label="970x250" style={{ width: "80%" }} />

                <h3 style={{width: '100%'}}>Mobile 300x250 (2 * 320x50) Template</h3>
                <TextInput fullwidth multiline source="combo_configuration.300x250.markup" label="300x250" style={{ width: "80%" }} />

            </FormTab>
            )}
            {!isTabDisabled('settings_tab_a2v') && (
            <FormTab label="A2V">
                <div style={twoColumnGrid}>
                    <SectionHeader title="Audio-to-Video Transcoding" />
                    <div style={fullWidth}>
                        Master controls for Audio-to-Video (A2V) just-in-time transcoding. When enabled, winning audio bids on video impressions are rewritten so the audio creative plays as a muxed mp4 (still image + audio) instead of the original mp3 that video players reject. Per-bidder participation is gated by <code>Bidder.a2v_enabled</code> on the bidder edit page.
                    </div>

                    <BooleanInput source="a2v_enabled" label="Enable A2V (master kill switch)" style={fullWidth}
                        helperText="When OFF, the adserver A2V rewriter is bypassed for every impression regardless of per-bidder a2v_enabled. Defaults to OFF — flip to ON to enable globally. Emergency disable: flip to OFF for instant rollback." />
                    <TextInput source="a2v_default_still_url" label="Default Still Image URL" style={fullWidth}
                        helperText="DO Spaces URL of the 640x360 placeholder image used when the audio creative's VAST has no <CompanionAd> image. Worker downloads this once per transcode and muxes it as the video track." />
                    <ArrayInput source="a2v_cache_buster_params" label="Cache-buster Query Params (stripped before hashing)" style={fullWidth}
                        helperText="Query-string parameter names removed from the mp3 URL before computing creativeKey. Two URLs that differ only in these params will share one cached mp4. Default list (when empty): cb, cachebuster, rand, _, timestamp, ts.">
                        <SimpleFormIterator>
                            <TextInput source="" label="Param name" />
                        </SimpleFormIterator>
                    </ArrayInput>
                </div>
            </FormTab>
            )}
            {/* <FormTab label="Auction Optimizer">
                <h3>General</h3>
                <BooleanInput source="auction_optimizer_config.active" label="Active" style={{width: '260px'}}/>
                <BooleanInput source="auction_optimizer_config.adserver_active" label="Ad Server Active" style={{width: '260px'}}/>
                <SelectInput source="auction_optimizer_config.test_quota" 
                choices={[
                    { id: 0, name: '0% - No Test Quota / Always Block' },
                    { id: 1, name: '1% - Allow 1% for testing' },
                    { id: 2, name: '2% - Allow 2% for testing' },
                    { id: 3, name: '3% - Allow 3% for testing' },
                    { id: 4, name: '4% - Allow 4% for testing' },
                    { id: 5, name: '5% - Allow 5% for testing' },
                    { id: 10, name: '10%' },
                    { id: 15, name: '15%' },
                    { id: 20, name: '20%' },
                    { id: 25, name: '25%' },
                    { id: 30, name: '30%' },
                    { id: 35, name: '35%' },
                    { id: 40, name: '40%' },
                    { id: 45, name: '45%' },
                    { id: 50, name: '50%' },
                    { id: 55, name: '55%' },
                    { id: 60, name: '60%' },
                    { id: 65, name: '65%' },
                    { id: 70, name: '70%' },
                    { id: 75, name: '75%' },
                    { id: 80, name: '80%' },
                    { id: 85, name: '85%' },
                    { id: 90, name: '90%' },
                    { id: 95, name: '95%' },
                    { id: 99, name: '99%' },
                    { id: 100, name: '100% - Always Allow' },
                ]}
                label="Test Quota" 
                helperText="Quota of traffic for which we do NOT apply the optimization."
                defaultValue={5}
                style={{width: '260px'}}/>
                <NumberInput source="auction_optimizer_config.max_records" label="Maximum Records" defaultValue={50000}/>

                <h4 style={{float: 'left', paddingLeft: '0px'}}>Date Range (Start &gt; End)</h4>
                <NumberInput source="auction_optimizer_config.start_day_offset" label="Start Day Offset"
                helperText="How far back should the report start."
                style={{float: 'left', paddingLeft: '10px'}} />
                
                <NumberInput source="auction_optimizer_config.end_day_offset" label="End Day Offset"
                helperText="How far back should the report stop."
                style={{float: 'left', paddingLeft: '10px'}} />
                
                <br/><br/>

                <h3>Auction Optimization Rules</h3>
                <ArrayInput source="auction_optimizer_config.rules"  style={{width: '60%'}} label="---">
				  <SimpleFormIterator>
                    <h4>Bidders Filter</h4>
                    <ReferenceArrayInput 
                        source="bidders_include" 
                        reference="bidders"
                        basePath="bidders"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ bidder_type:'RTB', search_name: searchText })}
                        style={{float: 'left', paddingLeft: '10px'}} >
                        <AutocompleteArrayInput
                        label="Included Bidders" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Bidders for which the Optimization is applied"
                        />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput 
                        source="bidders_exclude" 
                        reference="bidders"
                        basePath="bidders"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ bidder_type:'RTB', search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Excluded Bidders" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Bidders for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>
                    
                    <h4>Publishers Filter</h4>
                    <ReferenceArrayInput 
                        source="publishers_include" 
                        reference="publishers"
                        basePath="publishers"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Include Publishers" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Publishers for which the Optimization is applied"
                        />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput 
                        source="publishers_exclude" 
                        reference="publishers"
                        basePath="publishers"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Excluded Publishers" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Publishers for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>
                    
                    <h4>Sites Filter</h4>
                    <ReferenceArrayInput 
                        source="sites_include" 
                        reference="sites"
                        basePath="sites"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Include Sites" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Sites for which the Optimization is applied"
                        />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput 
                        source="sites_exclude" 
                        reference="sites"
                        basePath="sites"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Excluded Sites" 
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''} 
                        optionValue="code"
                        helperText="List of Sites for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>
                    <br/>
                    <h4>Metrics</h4>
                    <NumberInput source="response_rate_minimum" label="Response Rate Minimum"
                    helperText="Block inventory where response rate is less or equal to the minimum"/>
                    <NumberInput source="fill_rate_minimum" label="Fill Rate Minimum"
                    helperText="Block inventory where fill rate is less or equal to the minimum"/>
                    <SelectInput source="per_unit_cost_metric" label="Metric to use to compare to unit cost"
                    helperText="Block inventory where chosen metric is less or equal to the bid request unit cost"
                    choices={[
                        {id: 'revenue_per_bid_request_pm', name: 'Gross Revenue Per Bid Request'},
                        {id: 'nb_revenue_per_bid_request_pm', name: 'Platform Revenue Per Bid Request'},
                        {id: 'net_revenue_per_bid_request_pm', name: 'Publisher/Net Revenue Per Bid Request'}
                    ]}
                    allowEmpty
                    emptyText='- Do Not Apply - '
                    style={{width: '80%'}}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
            </FormTab> */}

            {!isTabDisabled('settings_tab_auction_optimizer') && (
            <FormTab label="Auction Optimizer">
                <div style={twoColumnGrid}>
                    <SectionHeader title="General" />
                    <BooleanInput source="auction_optimizer_v2_config.active" label="Active"
                        helperText="Master switch for the Auction Optimizer. When OFF, no optimization rules are computed or applied and all inventory is allowed." 
                        style={{marginBottom: '20px'}} />
                    <BooleanInput source="auction_optimizer_v2_config.adserver_active" label="Ad Server Active"
                        helperText="When ON, the ad server enforces the optimizer's block decisions on live traffic. When OFF, decisions are computed but not enforced (report/shadow mode)." />
                    
                    <SelectInput source="auction_optimizer_v2_config.test_quota"
                        choices={[
                            { id: 0, name: '0% - No Test Quota / Always Block' },
                            { id: 1, name: '1% - Allow 1% for testing' },
                            { id: 5, name: '5% - Allow 5% for testing' },
                            { id: 10, name: '10%' },
                            { id: 15, name: '15%' },
                            { id: 20, name: '20%' },
                            { id: 25, name: '25%' },
                            { id: 30, name: '30%' },
                            { id: 35, name: '35%' },
                            { id: 40, name: '40%' },
                            { id: 45, name: '45%' },
                            { id: 50, name: '50%' },
                            { id: 55, name: '55%' },
                            { id: 60, name: '60%' },
                            { id: 65, name: '65%' },
                            { id: 70, name: '70%' },
                            { id: 75, name: '75%' },
                            { id: 80, name: '80%' },
                            { id: 85, name: '85%' },
                            { id: 90, name: '90%' },
                            { id: 95, name: '95%' },
                            { id: 99, name: '99%' },
                            { id: 100, name: '100% - Always Allow' },
                        ]}
                        label="Test Quota"
                        helperText="Quota of traffic for which we do NOT apply the optimization."
                        defaultValue={5} />
                    <NumberInput source="auction_optimizer_v2_config.max_records" label="Maximum Records" defaultValue={50000} />

                    <SectionHeader title="Date Range (Start > End)" />
                    <NumberInput source="auction_optimizer_v2_config.start_day_offset" label="Start Day Offset"
                        helperText="How far back should the report start." />
                    <NumberInput source="auction_optimizer_v2_config.end_day_offset" label="End Day Offset"
                        helperText="How far back should the report stop." />

                    <SectionHeader title="Thresholds" />
                    <NumberInput source="auction_optimizer_v2_config.response_rate_minimum" label="Minimum Response Rate"
                        helperText="Block inventory where response rate is less or equal to the minimum" />
                    <NumberInput source="auction_optimizer_v2_config.fill_rate_minimum" label="Minimum Fill Rate"
                        helperText="Block inventory where fill rate is less or equal to the minimum" />
                    <NumberInput source="auction_optimizer_v2_config.net_profit_minimum" label="Minimum Net Profit"
                        helperText="Block inventory where net profit is less or equal to the minimum" />

                    <SectionHeader title="White Lists" />
                    <ReferenceArrayInput style={fullWidth}
                        label="White listed Publishers"
                        source="auction_optimizer_v2_config.white_listed_publishers"
                        reference="publishers"
                        basePath="publishers"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Include Publishers"
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''}
                        optionValue="code"
                        helperText="List of Publishers for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput style={fullWidth}
                        label="White listed Bidders"
                        source="auction_optimizer_v2_config.white_listed_bidders"
                        reference="bidders"
                        basePath="bidders"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Include Bidders"
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''}
                        optionValue="code"
                        helperText="List of Bidders for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput style={fullWidth}
                        label="White listed Sites"
                        source="auction_optimizer_v2_config.white_listed_sites"
                        reference="sites"
                        basePath="sites"
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        >
                        <AutocompleteArrayInput
                        label="Include Sites"
                        optionText={x => (x && x.code) ? `${x.name} (${x.code})` : ''}
                        optionValue="code"
                        helperText="List of Sites for which the Optimization is NOT applied"
                        />
                    </ReferenceArrayInput>

                    <SectionHeader title="Bidder Configuration" />
                    <ArrayInput source="auction_optimizer_v2_config.bidder_config" label="" style={fullWidth}>
                        <SimpleFormIterator>
                            <ReferenceArrayInput label="Bidder"
                                perPage={15}
                                sort={{ field: 'name', order: 'ASC' }}
                                filter={{ hidden: false }}
                                filterToQuery={searchText => ({ search_name: searchText })}
                                source="bidders"
                                reference="bidders"
                                basePath='bidders'>
                                <AutocompleteArrayInput optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ""} optionValue="code" style={{ width: '200px' }} />
                            </ReferenceArrayInput>
                            <NumberInput source="bidder_profit_factor" label="Bidder Profit Factor" helperText="1000000 * revenue * bidderProfitFactor / bidResponses" style={{width: '20%'}}/>
                            <NumberInput source="bidder_profit_per_million_minimum" label="Bidder Profit Minimum" helperText="Bidder Profit Factor > Bidder Profit Minimum" style={{width: '20%'}}/>
                        </SimpleFormIterator>
                    </ArrayInput>
                </div>
            </FormTab>
            )}

            {/* <FormTab label="Floor Bandit">
                <div style={twoColumnGrid}>
                    <SectionHeader title="Floor Bandit Config" />
                    <BooleanInput source="ml_floor_bandit_config.active" label="Active" />
                    <NumberInput source="ml_floor_bandit_config.bandit_allocation" label="Bandit Allocation %" />
                    <TextInput source="ml_floor_bandit_config.scalers" label="Scalers (comma-separated)"
                        format={v => v ? v.join(', ') : ''}
                        parse={v => v.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n))}
                        style={fullWidth} />
                    <NumberInput source="ml_floor_bandit_config.optimal_scaler" label="Optimal Scaler" disabled />
                    <NumberInput source="ml_floor_bandit_config.lookback_hours" label="Lookback Hours" />
                    <NumberInput source="ml_floor_bandit_config.min_ad_requests" label="Min Ad Requests" />
                    <NumberInput source="ml_floor_bandit_config.min_lift" label="Min Lift" />

                    <SectionHeader title="Last Run" />
                    <TextInput source="ml_floor_bandit_config.last_run.at" label="Last Run At" disabled />
                    <NumberInput source="ml_floor_bandit_config.last_run.winning_scaler" label="Winning Scaler" disabled />
                    <NumberInput source="ml_floor_bandit_config.last_run.winning_bucket" label="Winning Bucket" disabled />
                    <TextInput source="ml_floor_bandit_config.last_run.best_lift" label="Best Lift" disabled />
                </div>
            </FormTab> */}

            {/* <FormTab label="Monitoring">
                <div style={twoColumnGrid}>
                    <SectionHeader title="General" />
                    <BooleanInput source="monitoring.enabled" label="Monitoring Enabled" style={fullWidth} />

                    <SectionHeader title="Thresholds" />
                    <NumberInput source="monitoring.min_threshold" label="Minimum Hourly Revenue Percentage Threshold" defaultValue="50"
                        helperText="Hourly revenue should NOT drop below this percentage value." />
                    <NumberInput source="monitoring.max_threshold" label="Maximum Hourly Percentage Threshold" defaultValue={90}
                        helperText="Hourly revenue should NOT spike above this percentage value." />

                    <NumberInput source="monitoring.min_threshold_daily" label="Minimum Daily Revenue Percentage Threshold" defaultValue="50"
                        helperText="Daily revenue should NOT drop below this percentage value." />
                    <NumberInput source="monitoring.max_threshold_daily" label="Maximum Daily Percentage Threshold" defaultValue={90}
                        helperText="Daily revenue should NOT spike above this percentage value." />

                    <SectionHeader title="Notifications" />
                    <TextInput source="monitoring.email" label="Destination Notification Emails (comma separated)" style={fullWidth} />
                    <TextInput source="monitoring.digest_email" label="Publisher Daily Digest Emails (comma separated)" style={fullWidth} />

                    <SectionHeader title="Report Settings" />
                    <NumberInput source="monitoring.max_records" label="Maximum Top Publishers" defaultValue={50}
                        helperText="Number of Publishers to select at the TOP." />
                    <NumberInput source="monitoring.max_days" label="Maximum Days" defaultValue="30"
                        helperText="Number of days to pull the report for Publishers." />
                </div>
            </FormTab> */}
        </TabbedForm>

    </Edit>
    )
}
const CustomToolbar = props => (
	<Toolbar {...props}>
		<SaveButton />
		<br />
		<DateField source="updated_at" label="Update date" style={{ padding: '10px' }} showTime />
		<TextField source="updated_name" label="" style={{ padding: '10px' }} />
	</Toolbar>
);
